import { useEffect, useState, type FormEvent } from 'react';

import { Feedback } from './Feedback';
import { OverlayAlertView } from './OverlayAlertView';
import { TEMPLATE_LABELS } from '../lib/overlay-templates';
import {
  fetchOverlayConfig,
  regenerateOverlayToken,
  sendOverlayTestAlert,
  updateOverlaySettings,
  type OverlayConfig,
  type OverlayPosition,
  type OverlayTheme,
} from '../services/api';

/** Dados de exemplo usados na pré-visualização do alerta. */
const PREVIEW_ALERT = {
  senderName: 'Maria Fã',
  message: 'Parabéns pela live! Segue o apoio 💜',
  amountCents: 1500,
  senderPlatform: 'twitch' as const,
};

/** Grade 3×3 de posições do alerta na tela (padrão: rodapé/centro). */
const POSITION_GRID: { value: OverlayPosition; label: string }[] = [
  { value: 'top-left', label: '↖' },
  { value: 'top-center', label: '↑' },
  { value: 'top-right', label: '↗' },
  { value: 'middle-left', label: '←' },
  { value: 'middle-center', label: '●' },
  { value: 'middle-right', label: '→' },
  { value: 'bottom-left', label: '↙' },
  { value: 'bottom-center', label: '↓' },
  { value: 'bottom-right', label: '↘' },
];

const CUSTOM_HTML_EXAMPLE = `<div style="background:#111; color:#fff; padding:16px; border-radius:12px">
  <strong>{{nome}}</strong> mandou <strong>{{valor}}</strong><br/>
  {{mensagem}}
</div>`;

/**
 * Aba "Overlay (OBS)" da dashboard: URL secreta, teste, duração/valor
 * mínimo e a APARÊNCIA do alerta — 8 templates prontos totalmente
 * customizáveis (cores, logo, posição) + modo HTML próprio, com
 * pré-visualização ao vivo.
 */
export function OverlayConfigTab() {
  const [config, setConfig] = useState<OverlayConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [durationSeconds, setDurationSeconds] = useState('8');
  const [minAmountReais, setMinAmountReais] = useState('0,00');
  const [theme, setTheme] = useState<OverlayTheme | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchOverlayConfig()
      .then((data) => {
        setConfig(data);
        setDurationSeconds(String(data.settings.durationSeconds));
        setMinAmountReais((data.settings.minAmountCents / 100).toFixed(2).replace('.', ','));
        setTheme(data.theme);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Erro ao carregar o overlay.'),
      );
  }, []);

  if (error && !config) return <Feedback kind="error">{error}</Feedback>;
  if (!config || !theme) return <p className="page-loading">Carregando…</p>;

  // URL completa que vai no Browser Source do OBS
  const overlayUrl = `${window.location.origin}/overlay/${config.overlayToken}`;

  /** Atualiza um campo do tema mantendo os demais. */
  function patchTheme(partial: Partial<OverlayTheme>) {
    setTheme((current) => (current ? { ...current, ...partial } : current));
  }

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(overlayUrl);
      setNotice('URL copiada! Cole no Browser Source do OBS.');
    } catch {
      setNotice('Não foi possível copiar automaticamente — selecione e copie a URL acima.');
    }
  }

  async function testAlert() {
    setError(null);
    try {
      await sendOverlayTestAlert();
      setNotice('Alerta de teste enviado! Confira a tela do OBS (ou a página do overlay aberta).');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar o alerta de teste.');
    }
  }

  async function saveSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setIsSaving(true);

    const minAmountCents = Math.round(
      Number(minAmountReais.replace(/\./g, '').replace(',', '.')) * 100,
    );

    try {
      const updated = await updateOverlaySettings({
        durationSeconds: Number(durationSeconds),
        minAmountCents: Number.isNaN(minAmountCents) ? 0 : minAmountCents,
        theme: theme ?? undefined,
      });
      setConfig(updated);
      setTheme(updated.theme);
      setNotice('Configurações salvas! Valem já para o próximo alerta, sem reiniciar o OBS.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar as configurações.');
    } finally {
      setIsSaving(false);
    }
  }

  async function regenerate() {
    const confirmed = window.confirm(
      'Regenerar o token invalida a URL atual — você precisará colar a nova URL no OBS. Continuar?',
    );
    if (!confirmed) return;

    setError(null);
    try {
      const updated = await regenerateOverlayToken();
      setConfig(updated);
      setNotice('Novo token gerado. Atualize a URL no Browser Source do OBS.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao regenerar o token.');
    }
  }

  return (
    <section aria-label="Configuração do overlay do OBS" className="overlay-config">
      <div className="card">
        <h2 className="section-title section-title--first">Alertas na sua live</h2>
        <p className="overlay-config__text">
          Cada mensagem recebida aparece automaticamente na sua transmissão. Basta adicionar a URL
          abaixo como <strong>fonte de navegador (Browser Source)</strong> no OBS.
        </p>

        <label className="form__label" htmlFor="overlay-url">
          URL do seu overlay (mantenha em segredo)
        </label>
        <div className="overlay-config__url-row">
          <input
            className="form__input overlay-config__url"
            id="overlay-url"
            type="text"
            readOnly
            value={overlayUrl}
            onFocus={(event) => event.target.select()}
          />
          <button className="button button--small" type="button" onClick={copyUrl}>
            Copiar
          </button>
        </div>

        <div className="overlay-config__actions">
          <button className="button" type="button" onClick={testAlert}>
            Enviar alerta de teste
          </button>
          <button className="button button--ghost" type="button" onClick={regenerate}>
            Regenerar token
          </button>
        </div>
      </div>

      <form className="card form" onSubmit={saveSettings}>
        <h2 className="section-title section-title--first">Aparência do alerta</h2>

        {/* ---- Pré-visualização ao vivo ---- */}
        <div className="theme-preview" aria-label="Pré-visualização do alerta">
          <div className={`theme-preview__stage theme-preview__stage--${theme.position}`}>
            <OverlayAlertView theme={theme} alert={PREVIEW_ALERT} />
          </div>
          <p className="form__help">Pré-visualização — é assim que o alerta aparece na live.</p>
        </div>

        {/* ---- Galeria de templates ---- */}
        <div className="form__field">
          <span className="form__label">Template</span>
          <div className="template-gallery">
            {(Object.keys(TEMPLATE_LABELS) as OverlayTheme['template'][]).map((id) => (
              <button
                key={id}
                type="button"
                className={`template-gallery__option${
                  theme.template === id ? ' template-gallery__option--active' : ''
                }`}
                onClick={() => patchTheme({ template: id })}
              >
                {TEMPLATE_LABELS[id]}
              </button>
            ))}
          </div>
        </div>

        {/* ---- Cores e logo ---- */}
        <div className="theme-colors">
          <label className="theme-colors__item">
            <span className="form__label">Destaque</span>
            <input
              type="color"
              value={theme.accentColor}
              onChange={(event) => patchTheme({ accentColor: event.target.value })}
            />
          </label>
          <label className="theme-colors__item">
            <span className="form__label">Fundo</span>
            <input
              type="color"
              value={theme.backgroundColor}
              onChange={(event) => patchTheme({ backgroundColor: event.target.value })}
            />
          </label>
          <label className="theme-colors__item">
            <span className="form__label">Texto</span>
            <input
              type="color"
              value={theme.textColor}
              onChange={(event) => patchTheme({ textColor: event.target.value })}
            />
          </label>
        </div>

        <div className="form__field">
          <label className="form__label" htmlFor="logo-url">
            Logo (URL de imagem, opcional)
          </label>
          <input
            className="form__input"
            id="logo-url"
            type="url"
            placeholder="https://exemplo.com/meu-logo.png"
            value={theme.logoUrl}
            onChange={(event) => patchTheme({ logoUrl: event.target.value })}
          />
        </div>

        {/* ---- Posição na tela ---- */}
        <div className="form__field">
          <span className="form__label">Posição na tela (padrão: rodapé)</span>
          <div className="position-grid" role="group" aria-label="Posição do alerta">
            {POSITION_GRID.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                aria-label={`Posição ${value}`}
                className={`position-grid__cell${
                  theme.position === value ? ' position-grid__cell--active' : ''
                }`}
                onClick={() => patchTheme({ position: value })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ---- HTML próprio ---- */}
        {theme.template === 'custom' && (
          <div className="form__field">
            <label className="form__label" htmlFor="custom-html">
              Seu template HTML
            </label>
            <textarea
              className="form__textarea form__textarea--code"
              id="custom-html"
              rows={8}
              placeholder={CUSTOM_HTML_EXAMPLE}
              value={theme.customHtml}
              onChange={(event) => patchTheme({ customHtml: event.target.value })}
            />
            <p className="form__help">
              Placeholders disponíveis: <code>{'{{nome}}'}</code>, <code>{'{{mensagem}}'}</code>,{' '}
              <code>{'{{valor}}'}</code>, <code>{'{{plataforma}}'}</code> e{' '}
              <code>{'{{logo_url}}'}</code>. O conteúdo enviado pelos espectadores entra sempre como
              texto (sem HTML), por segurança.
            </p>
          </div>
        )}

        <h2 className="section-title">Exibição</h2>

        <div className="form__field">
          <label className="form__label" htmlFor="duration">
            Duração de cada alerta (segundos)
          </label>
          <input
            className="form__input"
            id="duration"
            type="number"
            min={3}
            max={60}
            required
            value={durationSeconds}
            onChange={(event) => setDurationSeconds(event.target.value)}
          />
        </div>

        <div className="form__field">
          <label className="form__label" htmlFor="minAmount">
            Valor mínimo para aparecer na live (R$)
          </label>
          <input
            className="form__input"
            id="minAmount"
            type="text"
            inputMode="decimal"
            value={minAmountReais}
            onChange={(event) => setMinAmountReais(event.target.value)}
          />
          <p className="form__help">
            Mensagens abaixo desse valor continuam na dashboard, só não aparecem na tela. Use 0,00
            para exibir todas.
          </p>
        </div>

        {notice && <Feedback kind="success">{notice}</Feedback>}
        {error && <Feedback kind="error">{error}</Feedback>}

        <button className="button button--primary" type="submit" disabled={isSaving}>
          {isSaving ? 'Salvando…' : 'Salvar configurações'}
        </button>
      </form>

      <div className="card">
        <h2 className="section-title section-title--first">Como configurar no OBS</h2>
        <ol className="overlay-config__steps">
          <li>
            No OBS, em <strong>Fontes</strong>, clique em <strong>+</strong> e escolha{' '}
            <strong>Navegador</strong> (Browser Source).
          </li>
          <li>Cole a URL do seu overlay no campo URL.</li>
          <li>
            Defina largura <strong>1920</strong> e altura <strong>1080</strong> (ou a resolução da
            sua live).
          </li>
          <li>
            Clique em OK e use o botão <strong>Enviar alerta de teste</strong> acima para conferir.
          </li>
        </ol>

        <h3 className="overlay-config__subtitle">Prefere automático? Use o plugin</h3>
        <p className="overlay-config__text">
          Baixe o plugin e instale no OBS em <strong>Ferramentas → Scripts</strong>: ele cria a
          fonte do overlay na cena atual, verifica a conexão com a plataforma e dispara alertas de
          teste sem sair do OBS.
        </p>
        <div className="overlay-config__actions">
          <a className="button button--ghost" href="/obs/send-message-obs-plugin.zip" download>
            Baixar plugin (.zip com instruções)
          </a>
          <a className="button button--ghost" href="/obs" target="_blank" rel="noreferrer">
            Página de instalação
          </a>
        </div>
      </div>
    </section>
  );
}
